/**
 * Interview Assessment Slice
 * Manages state for interview assessment feedback
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  AssessmentPayload,
  AssessmentSubmissionResponse,
  AssessmentFeedbackResponse,
  AssessmentStatus,
  POLLING_CONFIG,
  PROGRESS_MESSAGES,
} from "@/types/interview-feedback.types";
import {
  submitAssessment as submitAssessmentAPI,
  pollAssessmentStatus as pollAssessmentStatusAPI,
  getAssessmentFeedback as getAssessmentFeedbackAPI,
} from "@/services/api/endpoints/interview-assessment.api";
import { logger } from "@/utils/logger";

// ============================================================================
// State Interface
// ============================================================================

interface CachedFeedback {
  data: AssessmentFeedbackResponse;
  timestamp: number;
}

export interface InterviewAssessmentState {
  // Submission state
  isSubmitting: boolean;
  submissionError: string | null;

  // Assessment data
  assessmentId: string | null;
  status: AssessmentStatus | null;

  // Polling state
  isPolling: boolean;
  pollAttempts: number;
  progressMessage: string;
  pollingError: string | null;

  // Progress steps state (like progressMessage)
  currentStep: number;
  completedSteps: number[];

  // Feedback data
  feedback: AssessmentFeedbackResponse | null;
  isFetchingFeedback: boolean;
  fetchFeedbackError: string | null;

  // Feedback cache (permanent)
  feedbackCache: { [assessmentId: string]: CachedFeedback };
}

const initialState: InterviewAssessmentState = {
  isSubmitting: false,
  submissionError: null,

  assessmentId: null,
  status: null,

  isPolling: false,
  pollAttempts: 0,
  progressMessage: PROGRESS_MESSAGES[0],
  pollingError: null,

  currentStep: 0,
  completedSteps: [],

  feedback: null,
  isFetchingFeedback: false,
  fetchFeedbackError: null,

  feedbackCache: {},
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Submit assessment for processing
 */
export const submitAssessment = createAsyncThunk(
  "interviewAssessment/submit",
  async (payload: AssessmentPayload, { rejectWithValue }) => {
    try {
      logger.log("[submitAssessment] Submitting with metadata:", {
        meeting_id: payload.meeting_id,
        meeting_name: payload.meeting_name,
        meeting_category: payload.meeting_category,
      });

      const response = await submitAssessmentAPI(payload);
      return response;
    } catch (error: any) {
      logger.error("[Assessment] Submission failed:", error);

      // Extract error message safely
      const errorMessage =
        error?.message ||
        error?.error?.message ||
        (typeof error === "string" ? error : "Failed to submit assessment");

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Poll assessment status with automatic retry
 */
export const pollAssessmentStatus = createAsyncThunk(
  "interviewAssessment/pollStatus",
  async (
    payload: { assessmentId: string; meetingSession: string },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const { assessmentId, meetingSession } = payload;
      const response = await pollAssessmentStatusAPI(
        assessmentId,
        meetingSession
      );

      // Update progress message based on attempts
      const state = getState() as {
        interviewAssessment: InterviewAssessmentState;
      };
      const attempts = state.interviewAssessment.pollAttempts + 1;
      const messageIndex = Math.min(
        Math.floor((attempts - 1) / POLLING_CONFIG.MESSAGE_UPDATE_INTERVAL),
        PROGRESS_MESSAGES.length - 1
      );
      dispatch(setProgressMessage(PROGRESS_MESSAGES[messageIndex]));

      return response;
    } catch (error: any) {
      logger.error("[Assessment] Poll failed:", error);
      return rejectWithValue(error.message || "Failed to check status");
    }
  }
);

/**
 * Fetch complete assessment feedback (with cache support)
 */
export const fetchAssessmentFeedback = createAsyncThunk(
  "interviewAssessment/fetchFeedback",
  async (assessmentId: string, { rejectWithValue, getState }) => {
    try {
      // Check cache first
      const state = getState() as {
        interviewAssessment: InterviewAssessmentState;
      };
      const cachedFeedback =
        state.interviewAssessment.feedbackCache[assessmentId];

      if (cachedFeedback) {
        logger.log("[Assessment] Using cached feedback:", assessmentId);
        // Return cached data with flag to indicate it's from cache
        return { ...cachedFeedback.data, _fromCache: true };
      }

      logger.log("[Assessment] Fetching fresh feedback:", assessmentId);
      const response = await getAssessmentFeedbackAPI(assessmentId);
      return response;
    } catch (error: any) {
      logger.error("[Assessment] Fetch feedback failed:", error);
      return rejectWithValue(error.message || "Failed to load feedback");
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const interviewAssessmentSlice = createSlice({
  name: "interviewAssessment",
  initialState,
  reducers: {
    /**
     * Set progress message during polling
     */
    setProgressMessage(state, action: PayloadAction<string>) {
      state.progressMessage = action.payload;
    },

    /**
     * Increment poll attempts counter
     */
    incrementPollAttempts(state) {
      state.pollAttempts += 1;
    },

    /**
     * Reset poll attempts
     */
    resetPollAttempts(state) {
      state.pollAttempts = 0;
    },

    /**
     * Set polling error
     */
    setPollingError(state, action: PayloadAction<string>) {
      state.pollingError = action.payload;
      state.isPolling = false;
    },

    /**
     * Reset entire assessment state
     */
    resetAssessment(state) {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Submit Assessment
    builder.addCase(submitAssessment.pending, (state) => {
      state.isSubmitting = true;
      state.submissionError = null;
      // Reset polling state for new submission
      state.pollAttempts = 0;
      state.status = null;
      state.pollingError = null;
      state.isPolling = false;
      state.currentStep = 0;
      state.completedSteps = [];
      state.progressMessage = PROGRESS_MESSAGES[0];
    });
    builder.addCase(
      submitAssessment.fulfilled,
      (state, action: PayloadAction<AssessmentSubmissionResponse>) => {
        state.isSubmitting = false;
        state.assessmentId = action.payload.assessment_id;
        state.status = action.payload.status;
        logger.log("[Assessment] Submission successful:", {
          assessment_id: action.payload.assessment_id,
          status: action.payload.status,
        });
      }
    );
    builder.addCase(submitAssessment.rejected, (state, action) => {
      state.isSubmitting = false;
      state.submissionError = action.payload as string;
    });

    // Poll Assessment Status
    builder.addCase(pollAssessmentStatus.pending, (state) => {
      state.isPolling = true;
      state.pollingError = null;
    });
    builder.addCase(pollAssessmentStatus.fulfilled, (state, action) => {
      state.isPolling = false;
      state.status = action.payload.status;
      state.pollAttempts += 1;

      // Update progress steps every 2 polls (4 seconds)
      const attempts = state.pollAttempts;
      if (attempts > 0 && attempts % 2 === 0) {
        const stepToComplete = Math.floor(attempts / 2) - 1;

        // Complete step if valid and not already completed
        if (
          stepToComplete >= 0 &&
          stepToComplete < 3 &&
          !state.completedSteps.includes(stepToComplete)
        ) {
          state.completedSteps.push(stepToComplete);
          state.currentStep = stepToComplete + 1;
        }
      }

      if (action.payload.status === "completed") {
        logger.log("[Assessment] Processing completed");
        state.progressMessage = "Analysis completed successfully!";
        // Complete all remaining steps
        state.completedSteps = [0, 1, 2, 3];
        state.currentStep = 3;
      } else if (action.payload.status === "error") {
        logger.error("[Assessment] Processing failed");
        state.pollingError = "Assessment processing failed";
      }
    });
    builder.addCase(pollAssessmentStatus.rejected, (state, action) => {
      state.isPolling = false;
      state.pollingError = action.payload as string;
    });

    // Fetch Assessment Feedback
    builder.addCase(fetchAssessmentFeedback.pending, (state) => {
      state.isFetchingFeedback = true;
      state.fetchFeedbackError = null;
    });
    builder.addCase(
      fetchAssessmentFeedback.fulfilled,
      (
        state,
        action: PayloadAction<
          AssessmentFeedbackResponse & { _fromCache?: boolean }
        >
      ) => {
        const isFromCache = action.payload._fromCache;
        const feedbackData = { ...action.payload };
        delete feedbackData._fromCache;

        state.isFetchingFeedback = false;
        state.feedback = feedbackData as AssessmentFeedbackResponse;

        // Cache the feedback permanently (if not already cached)
        if (!isFromCache && feedbackData.assessment_id) {
          state.feedbackCache[feedbackData.assessment_id] = {
            data: feedbackData as AssessmentFeedbackResponse,
            timestamp: Date.now(),
          };
          logger.log(
            "[Assessment] Feedback cached:",
            feedbackData.assessment_id
          );
        }

        logger.log("[Assessment] Feedback loaded:", {
          blocks_count: feedbackData.interviewPracticeFeedback.blocks.length,
          from_cache: isFromCache,
        });
      }
    );
    builder.addCase(fetchAssessmentFeedback.rejected, (state, action) => {
      state.isFetchingFeedback = false;
      state.fetchFeedbackError = action.payload as string;
    });
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setProgressMessage,
  incrementPollAttempts,
  resetPollAttempts,
  setPollingError,
  resetAssessment,
} = interviewAssessmentSlice.actions;

export default interviewAssessmentSlice.reducer;
